import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import type { Item } from 'webapp/components/form/range/list-base';

import DateRange from 'webapp/models-custom/date-range';
import type DsCompare from 'webapp/models-custom/ds-compare';
import type Granulation from 'webapp/models-custom/granulation';

export interface Args {
	model: Item;
	isSelected: boolean;
	onSelect: (item: Item, skipClose?: boolean) => void;
	hideUnsupported?: boolean;
	theme?: string;
}

export default abstract class FormListItemBaseComponent<T extends Args> extends Component<T> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	get maxMultiplier(): number | undefined {
		return 1; // relevant only for range, which will implement its own
	}

	get hasExtra() {
		return 'hasExtraSettings' in this.args.model && this.args.model.hasExtraSettings; // granulations have none
	}

	get showExtra() {
		return this.args.isSelected && this.hasExtra && !this.args.model.isDisabled;
	}

	@cached
	get limitationInfo() {
		const model = this.args.model as DateRange | Granulation;
		return this.mmMetricsService.getLimitationInfoForDateRangeOrGranulation(
			model,
			this.currentUserService.isAdmin,
		);
	}

	get showInfoTextAtTitle() {
		return this.limitationInfo.text && (this.args.model.isDisabled || !this.hasExtra);
	}

	get maxMultiplierWarningText() {
		return this._getMaxMultiplierWarningText('max', this.maxMultiplier);
	}

	_getMaxMultiplierWarningText(type: string, value: number | undefined) {
		let text;

		// only show this for max values
		if (type === 'max') {
			const timeUnit =
				(this.args.model as DateRange).rangeTimeUnit ||
				(this.args.model as DsCompare).timeUnit;
			const periodData = timeUnit
				? DateRange.baseGranulations.find((item) => item.id === timeUnit)
				: null; // both range and compare
			let periodName = (periodData ? periodData.title : '').toLowerCase();
			if (periodName && value !== 1) {
				periodName += 's';
			}
			text = `Your billing plan allows a ${type}imum of ${value} ${periodName} of data.`;
		}

		return text;
	}

	@action
	select() {
		if (!this.args.model.isDisabled) {
			this.args.onSelect(this.args.model, this.hasExtra);
		}
	}
}
